import type {
  CreateRoomRequest,
  CreateRoomResponse,
  IceConfig,
  JoinRoomRequest,
  ParticipantView,
  RoomPreview,
  TelemetryRequest,
  TranslationSessionResponse,
  VoiceListResponse,
  VoiceSessionResponse,
} from '@voxa/shared';

/**
 * The REST client. Every call is same-origin and credentialed, because the
 * participant credential is an HttpOnly cookie the page cannot read.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text === '' ? null : safeJson(text);

  if (!res.ok) {
    const err = body as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      err?.error?.code ?? 'http_error',
      err?.error?.message ?? `Request failed (${res.status})`,
      res.status,
    );
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  login: (accessCode: string) =>
    request<void>('/api/login', { method: 'POST', body: JSON.stringify({ accessCode }) }),

  logout: () => request<void>('/api/logout', { method: 'POST' }),

  createRoom: (body: CreateRoomRequest) =>
    request<CreateRoomResponse>('/api/rooms', { method: 'POST', body: JSON.stringify(body) }),

  previewRoom: (roomId: string, invite: string) =>
    request<RoomPreview>(`/api/rooms/${roomId}/preview?invite=${encodeURIComponent(invite)}`),

  joinRoom: (roomId: string, body: JoinRoomRequest) =>
    request<ParticipantView>(`/api/rooms/${roomId}/join`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  refreshIce: (roomId: string) =>
    request<IceConfig>(`/api/rooms/${roomId}/ice`, { method: 'POST' }),

  translationSession: (roomId: string) =>
    request<TranslationSessionResponse>(`/api/rooms/${roomId}/translation-session`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  voices: (language: string) =>
    request<VoiceListResponse>(`/api/voices?language=${encodeURIComponent(language)}`),

  voiceSession: (roomId: string) =>
    request<VoiceSessionResponse>(`/api/rooms/${roomId}/voice-session`, { method: 'POST' }),

  leaveRoom: (roomId: string) => request<void>(`/api/rooms/${roomId}/leave`, { method: 'POST' }),

  telemetry: (body: TelemetryRequest) =>
    request<void>('/api/telemetry', { method: 'POST', body: JSON.stringify(body) }),
};
